import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import CustomSelect from '../../../../widgets/CustomSelect/CustomSelect';
import FormSubsection from '../../../../widgets/FormSubsection/FormSubsection';
import CustomValueModal from '../../../../widgets/CustomValueModal/CustomValueModal';
import { DialogueTypes } from '../../../../widgets/CustomValueModal/definitions';
import { cloneDeep } from 'lodash';
import {
  getRenewalPriceOptions,
  createPriceLabel
} from '../../../../../utils/renewals-util';
import {
  containsMutableField,
  PRODUCT_LINE_ITEM_FIELDS
} from '../line-item-page-util';
import { isFieldVisible } from '../../../../../utils/bill-method-util';
import { injectIntl } from 'react-intl';
import { Hidden } from '@material-ui/core';
import CustomTextInput from '../../../../widgets/CustomTextInput/CustomTextInput';

const ENABLE_MAD_RATE = 'ENABLE_MAD_RATE';
const ENABLE_REPLACE_RATE_UPDATE = 'ENABLE_REPLACE_RATE_UPDATE';

class ProductPricingPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      disabled: true,
      priceItemOptions: [],
      priceRentalOptions: [],
      priceWashOptions: [],
      priceReplaceOptions: [],
      adjustedValue: null,
      appConfigs: this.props.appStore.appConfigs
    };
    this.originalValues = {
      imageCarePrice: props.lineItem.imageCarePrice
    };
    this.toggleDisabled = this.toggleDisabled.bind(this);
    this.handleAccept = this.handleAccept.bind(this);
    this.toggleAdjustmentPopup = this.toggleAdjustmentPopup.bind(this);
    this.showAdjustmentPopup = this.showAdjustmentPopup.bind(this);
    this.handleChangeLineItem = this.handleChangeLineItem.bind(this);
    this.updatePriceOptions = this.updatePriceOptions.bind(this);
    this.generatePriceOptions = this.generatePriceOptions.bind(this);
  }

  componentDidMount() {
    this.updatePriceOptions(this.props);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.lineItem.adjustedPrice !== prevProps.lineItem.adjustedPrice
    ) {
      this.updatePriceOptions(this.props);
    }
  }

  generatePriceOptions(
    originalPrice,
    adjustedPrice,
    showMadRate = false,
    madRate
  ) {
    const results = getRenewalPriceOptions(
      originalPrice,
      adjustedPrice,
      this.state.appConfigs['MAX_BASE_PRICE_INCREASE']?.value,
      showMadRate,
      madRate
    );
    return results.map((result) => ({
      ...result,
      label: createPriceLabel(
        result.value,
        !!result.labelId
          ? this.props.intl.formatMessage({
              id: result.labelId
            })
          : null
      )
    }));
  }

  updatePriceOptions(prop) {
    this.setState({
      priceItemOptions: this.generatePriceOptions(
        prop.lineItem.originalPrice.priceItem,
        prop.lineItem.adjustedPrice.priceItem
      ),
      priceRentalOptions: this.generatePriceOptions(
        prop.lineItem.originalPrice.priceRental,
        prop.lineItem.adjustedPrice.priceRental,
        this.state.appConfigs[ENABLE_MAD_RATE]?.value === 'true',
        prop.lineItem.originalPrice.rentalMadRate
      ),
      priceWashOptions: this.generatePriceOptions(
        prop.lineItem.originalPrice.priceWash,
        prop.lineItem.adjustedPrice.priceWash
      ),
      priceReplaceOptions: this.generatePriceOptions(
        prop.lineItem.originalPrice.priceReplace,
        prop.lineItem.adjustedPrice.priceReplace
      )
    });
  }

  toggleDisabled() {
    this.setState({
      disabled: !this.state.disabled || this.props.disabled
    });
  }

  showAdjustmentPopup(fieldName, value) {
    this.setState({
      showAdjustmentPopup: true,
      adjustmentPopupField: fieldName,
      adjustedValue: value
    });
  }

  toggleAdjustmentPopup() {
    this.setState({
      showAdjustmentPopup: !this.state.showAdjustmentPopup
    });
  }

  handleAccept(value, adjustmentReason) {
    const lineItem = cloneDeep(this.props.lineItem);
    lineItem.adjustedPrice[this.state.adjustmentPopupField] = value;
    lineItem.priceAdjustmentReason = adjustmentReason;
    this.props.handleChangeLineItem(lineItem);
    this.setState({
      showAdjustmentPopup: false
    });
  }

  handleChangeLineItem(fieldName, value) {
    if (this.props.fieldChangeRequiresApproval(fieldName)) {
      this.showAdjustmentPopup(fieldName, value);
    } else {
      const lineItem = PRODUCT_LINE_ITEM_FIELDS[fieldName].getUpdatedLineItem(
        this.props.lineItem,
        value
      );
      this.props.handleChangeLineItem(lineItem);
    }
  }

  render() {
    const priceItemName = PRODUCT_LINE_ITEM_FIELDS.priceItem.name;
    const priceRentalName = PRODUCT_LINE_ITEM_FIELDS.priceRental.name;
    const priceWashName = PRODUCT_LINE_ITEM_FIELDS.priceWash.name;
    const priceReplaceName = PRODUCT_LINE_ITEM_FIELDS.priceReplace.name;
    const easyCarePriceName = PRODUCT_LINE_ITEM_FIELDS.easyCarePrice.name;
    const easyCarePriceField = PRODUCT_LINE_ITEM_FIELDS.easyCarePrice.field;

    const mutableFields = {
      [priceItemName]: this.props.isFieldEditable(
        priceItemName,
        this.props.lineItem
      ),
      [priceRentalName]: this.props.isFieldEditable(
        priceRentalName,
        this.props.lineItem
      ),
      [priceWashName]: this.props.isFieldEditable(
        priceWashName,
        this.props.lineItem
      ),
      [priceReplaceName]:
        this.props.isFieldEditable(priceReplaceName, this.props.lineItem) &&
        this.state.appConfigs[ENABLE_REPLACE_RATE_UPDATE]?.value === 'true',
      [easyCarePriceName]: this.props.isFieldEditable(
        easyCarePriceName,
        this.props.lineItem
      )
    };

    return (
      <FormSubsection
        title="Product Pricing"
        disabled={this.state.disabled}
        toggleDisabled={this.toggleDisabled}
        showEdit={containsMutableField(mutableFields) && !this.props.disabled && !(this.state.appConfigs['DISABLE_RENEWAL_EDITS']?.value === 'true')}
      >
        <div className="row field-container">
          {isFieldVisible(
            priceItemName,
            this.props.lineItem.billMethod,
            this.props.lineItem.adjustedPrice[priceItemName]
          ) ? (
            <div
              className="
                      col-xs-12
                      col-sm-3
                      padding-wrapper"
            >
              <CustomSelect
                labelText={PRODUCT_LINE_ITEM_FIELDS.priceItem.label}
                disabled={this.state.disabled || !mutableFields[priceItemName]}
                handleChange={(value) =>
                  this.handleChangeLineItem(priceItemName, value)
                }
                handleCustomSelected={() =>
                  this.showAdjustmentPopup(priceItemName)
                }
                options={this.state.priceItemOptions}
                originalValue={this.props.lineItem.originalPrice.priceItem}
                selectedValue={this.props.lineItem.adjustedPrice[priceItemName]}
                startAdornment="$"
                approvalRequired={this.props.fieldChangeRequiresApproval(
                  priceItemName
                )}
              />
            </div>
          ) : null}
          {isFieldVisible(
            priceRentalName,
            this.props.lineItem.billMethod,
            this.props.lineItem.adjustedPrice[priceRentalName]
          ) ? (
            <div
              className="
                      col-xs-12
                      col-sm-3
                      padding-wrapper"
            >
              <CustomSelect
                location={this.props.location}
                labelText={PRODUCT_LINE_ITEM_FIELDS.priceRental.label}
                disabled={
                  this.state.disabled || !mutableFields[priceRentalName]
                }
                handleChange={(value) =>
                  this.handleChangeLineItem(priceRentalName, value)
                }
                handleCustomSelected={() =>
                  this.showAdjustmentPopup(priceRentalName)
                }
                options={this.state.priceRentalOptions}
                originalValue={this.props.lineItem.originalPrice.priceRental}
                selectedValue={
                  this.props.lineItem.adjustedPrice[priceRentalName]
                }
                startAdornment="$"
                approvalRequired={this.props.fieldChangeRequiresApproval(
                  priceRentalName
                )}
              />
            </div>
          ) : null}
          {isFieldVisible(
            priceWashName,
            this.props.lineItem.billMethod,
            this.props.lineItem.adjustedPrice[priceWashName]
          ) ? (
            <div
              className="
                      col-xs-12
                      col-sm-3
                      padding-wrapper"
            >
              <CustomSelect
                location={this.props.location}
                labelText={PRODUCT_LINE_ITEM_FIELDS.priceWash.label}
                disabled={this.state.disabled || !mutableFields[priceWashName]}
                handleChange={(value) =>
                  this.handleChangeLineItem(priceWashName, value)
                }
                handleCustomSelected={() =>
                  this.showAdjustmentPopup(priceWashName)
                }
                options={this.state.priceWashOptions}
                originalValue={this.props.lineItem.originalPrice.priceWash}
                selectedValue={this.props.lineItem.adjustedPrice[priceWashName]}
                startAdornment="$"
                approvalRequired={this.props.fieldChangeRequiresApproval(
                  priceWashName
                )}
              />
            </div>
          ) : null}
          {isFieldVisible(
            priceReplaceName,
            this.props.lineItem.billMethod,
            this.props.lineItem.adjustedPrice[priceReplaceName]
          ) ? (
            <div
              className="
                      col-xs-12
                      col-sm-3
                      padding-wrapper"
            >
              <CustomSelect
                location={this.props.location}
                labelText={PRODUCT_LINE_ITEM_FIELDS.priceReplace.label}
                disabled={
                  this.state.disabled || !mutableFields[priceReplaceName]
                }
                handleChange={(value) =>
                  this.handleChangeLineItem(priceReplaceName, value)
                }
                handleCustomSelected={() =>
                  this.showAdjustmentPopup(priceReplaceName)
                }
                options={this.state.priceReplaceOptions}
                originalValue={this.props.lineItem.originalPrice.priceReplace}
                selectedValue={
                  this.props.lineItem.adjustedPrice[priceReplaceName]
                }
                startAdornment="$"
                approvalRequired={this.props.fieldChangeRequiresApproval(
                  priceReplaceName
                )}
              />
            </div>
          ) : null}
          <Hidden
            xsUp={
              !isFieldVisible(
                easyCarePriceName,
                this.props.lineItem.billMethod,
                this.props.lineItem[easyCarePriceField],
                this.props.lineItem.typeOfMerchandise
              )
            }
          >
            <div
              className="
                      col-xs-12
                      col-sm-2
                      padding-wrapper"
            >
              <CustomTextInput
                labelText={PRODUCT_LINE_ITEM_FIELDS[easyCarePriceName].label}
                disabled={
                  this.state.disabled || !mutableFields[easyCarePriceName]
                }
                initialValue={this.originalValues.imageCarePrice}
                selectedValue={this.props.lineItem[easyCarePriceField]}
                isValidChange={(value) => value !== '' && value >= 0}
                onBlur={(value) => this.handleBlur(easyCarePriceName, value)}
                approvalRequired={false}
                startAdornment="$"
              />
            </div>
          </Hidden>
        </div>
        {this.state.showAdjustmentPopup ? (
          <CustomValueModal
            initialValue={
              this.props.lineItem.originalPrice[this.state.adjustmentPopupField]
            }
            adjustedValue={
              this.state.adjustedValue ||
              this.props.lineItem.adjustedPrice[this.state.adjustmentPopupField]
            }
            labelText={
              PRODUCT_LINE_ITEM_FIELDS[this.state.adjustmentPopupField].label
            }
            lineItemField={this.state.adjustmentPopupField}
            handleAccept={this.handleAccept}
            handleClose={this.toggleAdjustmentPopup}
            adjustmentReason={this.props.lineItem.priceAdjustmentReason}
            adjustmentReasons={this.props.priceReasonCodes}
            type={DialogueTypes.Price}
            startAdornment={'$'}
            isModalOpen={this.state.showAdjustmentPopup}
            lineItem={this.props.lineItem}
          />
        ) : null}
      </FormSubsection>
    );
  }
}

ProductPricingPanel.propTypes = {
  disabled: PropTypes.bool,
  lineItem: PropTypes.object,
  handleChangeLineItem: PropTypes.func,
  isFieldEditable: PropTypes.func,
  fieldChangeRequiresApproval: PropTypes.func,
  priceReasonCodes: PropTypes.array
};

const mapStateToProps = (state) => ({
  appStore: state.get('app').toJS()
});

export default connect(mapStateToProps)(injectIntl(ProductPricingPanel));
